import { useStoreRehydrated, useStoreState } from 'easy-peasy'

import { useAppConfig } from 'artkive/contexts/AppConfigContext'
import { PROMO_QUERY_FILED } from 'artkive/stores/product.constants'
import routes from 'artkive/utils/routes'

import SaleImage from 'images/ecom/virtual-gallery/countdown/primeday.jpg'

/**
 * Builds the box checkout link, preserving the given page query string so an
 * incoming promo (e.g. a referral link) always wins. The fallback code is
 * injected only when the query string carries no `promo` param, unless `force`
 * makes it override whatever is there.
 *
 * @param {string} search current query string, empty during SSR/first client render
 * @param {string} fallbackPromoCode store promo or sale default, may be empty
 * @param {boolean} force overrides an existing `promo` param with fallbackPromoCode
 * @returns {string}
 */
const buildBoxCheckoutUrl = (search, fallbackPromoCode, force = false) => {
  // string concat when there is no query string: the SSR sandbox (ExecJS) has
  // no URLSearchParams, and this branch is the only one SSR reaches
  if (!search) {
    return fallbackPromoCode
      ? `${routes.boxCheckout}?${PROMO_QUERY_FILED}=${encodeURIComponent(fallbackPromoCode)}`
      : routes.boxCheckout
  }

  const params = new URLSearchParams(search)

  if (fallbackPromoCode && (force || !params.get(PROMO_QUERY_FILED))) {
    params.set(PROMO_QUERY_FILED, fallbackPromoCode)
  }

  const query = params.toString()

  return query ? `${routes.boxCheckout}?${query}` : routes.boxCheckout
}

const DEFAULT_ANNOUNCEMENT_BAR = { background: '#28251F', color: '#FFFFFF' }
const DEFAULT_PROMO_BAR = { background: '#7A68AE', color: '#FFFFFF' }

/**
 * Single source of truth for the active marketing sale. All values are
 * recomputed server-side per request, so the sale turns on/off and the
 * price tier escalates automatically by date.
 *
 * `boxCheckoutUrl` resolves the promo as: URL param > promo store > sale default.
 * The URL/store parts are gated on store rehydration so the first client render
 * matches the SSR output (React 17 keeps the server attribute on hydration
 * mismatch); the post-rehydration re-render swaps in the real link.
 *
 * During an admin sale preview (`env.salePreview` present, admins only) the
 * previewed tier's code overrides both rungs, so preview links show the sale
 * exactly as a promo-less customer will get it.
 *
 * @returns {{
 *   isSale: boolean,
 *   name: string,
 *   priceLabel: string,
 *   promoCode: (string|null),
 *   boxCheckoutUrl: string,
 *   announcementBar: {background: string, color: string},
 *   promoBar: {background: string, color: string},
 *   countdownImageUrl: (string|null),
 * }}
 */
const useSale = () => {
  const {
    env: { sale, salePreview },
  } = useAppConfig()
  const storePromo = useStoreState(({ promoStore }) => promoStore.promo)
  const rehydrated = useStoreRehydrated()
  const isBrowser = typeof location !== 'undefined'

  const promoCode = sale?.promoCode ?? null
  const previewing = Boolean(salePreview)

  // 'sale'-typed codes defer to the current tier so an escalated sale is not
  // pinned to a stale persisted code; definitively-failed codes are skipped too
  const isReusableStorePromo =
    storePromo.code && storePromo.type !== 'sale' && (storePromo.validated || storePromo.requesting)
  const storePromoCode = rehydrated && isReusableStorePromo ? storePromo.code : ''
  const search = rehydrated && isBrowser ? location.search : ''

  return {
    isSale: Boolean(sale),
    name: sale?.name ?? '',
    priceLabel: sale?.priceLabel ?? '',
    promoCode,
    boxCheckoutUrl: previewing
      ? buildBoxCheckoutUrl(search, promoCode || '', true)
      : buildBoxCheckoutUrl(search, storePromoCode || promoCode || ''),
    announcementBar: sale?.announcementBar ?? DEFAULT_ANNOUNCEMENT_BAR,
    promoBar: sale?.promoBar ?? DEFAULT_PROMO_BAR,
    countdownImageUrl: sale ? sale.countdownImageUrl || SaleImage : null,
  }
}

export default useSale
